import React, { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import logo from "../../assets/logos/logo.jpeg";
import BlogAdmin from "./BlogAdmin";
import BlogAnalytics from "./BlogAnalytics";
import BlogEditor from "./BlogEditor";
import { contentRoleLabel } from "./contentAccess";
import "./ContentAdmin.css";

export const CONTENT_SIDEBAR_STORAGE_KEY =
  "thrive-insurance-content-sidebar-collapsed";

const NAV = [
  { to: "/content-admin/blog", label: "Blog posts", icon: "newspaper" },
  { to: "/content-admin/analytics", label: "Content analytics", icon: "chart-line" },
];

const readSidebarPreference = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CONTENT_SIDEBAR_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const saveSidebarPreference = (collapsed) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CONTENT_SIDEBAR_STORAGE_KEY,
      String(collapsed)
    );
  } catch {
    // Storage can be unavailable in private browsing or restricted embeds.
  }
};

const ContentAdmin = ({ contentUser, authToken }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    readSidebarPreference
  );
  const [, , removeCookie] = useCookies(["mytoken"]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const toggleSidebar = () => {
    const nextCollapsed = !sidebarCollapsed;
    setSidebarCollapsed(nextCollapsed);
    saveSidebarPreference(nextCollapsed);
  };

  const signOut = () => {
    removeCookie("mytoken", { path: "/" });
    navigate("/content-login", { replace: true });
  };

  return (
    <div
      data-testid="content-admin-shell"
      className={`travel-admin-shell content-admin-shell${
        sidebarCollapsed ? " is-sidebar-collapsed" : ""
      }`}
    >
      <button
        type="button"
        className="travel-admin-mobile-toggle"
        aria-label={menuOpen ? "Close content navigation" : "Open content navigation"}
        aria-controls="content-admin-sidebar"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((value) => !value)}
      >
        <i className={`fas fa-${menuOpen ? "xmark" : "bars"}`} aria-hidden="true" />
      </button>

      <aside
        id="content-admin-sidebar"
        className={`travel-admin-sidebar${menuOpen ? " is-open" : ""}${
          sidebarCollapsed ? " is-collapsed" : ""
        }`}
        aria-label="Content Studio navigation"
      >
        <NavLink
          className="travel-admin-brand"
          to="/content-admin/blog"
          aria-label="Content Studio home"
          title={sidebarCollapsed ? "Content Studio home" : undefined}
        >
          <img src={logo} alt="" />
          <span className="travel-admin-sidebar-label">Content Studio</span>
        </NavLink>

        <button
          type="button"
          className="travel-admin-sidebar-collapse"
          aria-label={
            sidebarCollapsed
              ? "Expand content navigation"
              : "Collapse content navigation"
          }
          aria-controls="content-admin-navigation"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? "Expand navigation" : undefined}
        >
          <i
            className={`fas fa-angles-${sidebarCollapsed ? "right" : "left"}`}
            aria-hidden="true"
          />
          <span className="travel-admin-sidebar-label">
            {sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          </span>
        </button>

        <nav id="content-admin-navigation" aria-label="Content Studio">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "is-active" : undefined)}
              aria-label={item.label}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <i className={`fas fa-${item.icon}`} aria-hidden="true" />
              <span className="travel-admin-sidebar-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="travel-admin-sidebar__footer">
          <Link
            to="/"
            className="travel-admin-public-link"
            aria-label="Public website"
            title={sidebarCollapsed ? "Public website" : undefined}
          >
            <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
            <span className="travel-admin-sidebar-label">Public website</span>
          </Link>
          <button
            type="button"
            onClick={signOut}
            aria-label="Sign out"
            title={sidebarCollapsed ? "Sign out" : undefined}
          >
            <i className="fas fa-right-from-bracket" aria-hidden="true" />
            <span className="travel-admin-sidebar-label">Sign out</span>
          </button>
        </div>
      </aside>

      {menuOpen && (
        <button
          type="button"
          className="travel-admin-backdrop"
          aria-label="Close content navigation"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="travel-admin-workspace">
        <header className="travel-admin-topbar">
          <div>
            <span>Signed in as</span>
            <strong>
              {contentUser?.first_name ||
                contentUser?.username ||
                "Content manager"}
            </strong>
          </div>
          <span className="travel-admin-role">{contentRoleLabel(contentUser)}</span>
        </header>
        <main className="travel-admin-main">
          <Routes>
            <Route index element={<Navigate replace to="blog" />} />
            <Route path="blog" element={<BlogAdmin token={authToken} />} />
            <Route path="analytics" element={<BlogAnalytics token={authToken} />} />
            <Route path="blog/new" element={<BlogEditor token={authToken} />} />
            <Route
              path="blog/:postId/edit"
              element={<BlogEditor token={authToken} />}
            />
            <Route path="*" element={<Navigate replace to="/content-admin/blog" />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ContentAdmin;
